import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useCallback,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { InputMask, format } from '@react-input/mask';
import TextField from '../TextField/TextField';
import { Box } from '@mui/material';

export const FedTypes = {
  Individual: 'IndividualFEDPattern',
  Other: 'OtherFEDPattern',
} as const;
export type FedType = (typeof FedTypes)[keyof typeof FedTypes];

const Masks: { [key in FedType]: string } = {
  [FedTypes.Individual]: '___-__-____',
  [FedTypes.Other]: '__-_______',
};

export function formatFed(value: string, type: FedType) {
  return format(value ?? '', { mask: Masks[type], replacement: { _: /\d/ } });
}

type Props = {
  value?: string;
  onChange?: (e: string) => unknown;
  fedType: FedType;
  label?: string;
  placeholder?: string;
  error?: boolean;
  tooltip?: ReactNode;
  required?: boolean;
  loading?: boolean;
  'data-testid'?: string;
};

const FederalIdInput = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, fedType, ...rest }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e.target.value);
      },
      [onChange]
    );

    const prevFedTypeRef = useRef(fedType);

    useEffect(() => {
      if (!value || !onChange || prevFedTypeRef.current === fedType) {
        prevFedTypeRef.current = fedType;
        return;
      }

      const formatted = formatFed(value, fedType);
      if (formatted !== value) {
        onChange(formatted);
      }

      prevFedTypeRef.current = fedType;
    }, [fedType, value, onChange]);

    return (
      <Box>
        <InputMask
          ref={inputRef}
          mask={Masks[fedType]}
          value={value}
          onChange={handleChange}
          replacement={{ _: /\d/ }}
          component={TextField}
          fullWidth
          {...rest}
        />
      </Box>
    );
  }
);

export default FederalIdInput;
